import {
    type ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    type SliceCaseReducers,
} from '@reduxjs/toolkit';

import type {
    IGlobalStore,
    IGlobalStoreReducers,
    SliceProps,
    SlicePropsWithData,
} from './initStore';
import type { IUserStamp, OAuthUserDataType } from '../pdf/Interfaces';
import api from '../api';
import { GlobalVariables } from '../pdf/GlobalVariables';
import { backendApiUrl } from '../App';
import { v4 as uuidv4 } from 'uuid';
import { ReduxUpdater } from '../pdf/ReduxUpdater';

/**
 * Application backend writes this variable to the page on load.
 * This global variable contains the URL that is used to log the user out of the application in Auth0.
 */
declare const auth0LogoutUri: string;

/**
 * Initial slice state value.
 */
const initialState: IUserSettingsState = {
    data: null,
    loading: true,
    error: null,
    picture: {
        loading: false,
        error: null,
    },
    maxProfilePictureSize: 2621440,
    maxStampPictureSize: 2621440,
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Fetches the user's settings from the backend.
     */
    getUserProfile: async (): Promise<OAuthUserDataType> => {
        try {
            const userProfile = await api.get<OAuthUserDataType>(backendApiUrl.user.profile);

            return userProfile ? userProfile : Promise.reject('No user profile data.');
        } catch (e) {
            return Promise.reject(e);
        }
    },

    /**
     * Updates the user's protection data in the backend.
     */
    updateUserProfileProtection: async (protect: string): Promise<string> => {
        /** Note: Backend only accepts data in form-format! */
        const data: FormData = new FormData();

        data.append('protect', protect);

        try {
            await api.post<void>(backendApiUrl.user.profileProtection, data);

            return Promise.resolve(protect);
        } catch (e) {
            return Promise.reject(e);
        }
    },

    /**
     * Updates the user's picture in the backend.
     */
    updateUserProfilePicture: async (image: File, thunkApi): Promise<string> => {
        /** Note: Backend only accepts data in form-format! */

        if (image.size > (thunkApi.getState() as IGlobalStore).userSettings.maxProfilePictureSize) {
            return Promise.reject('Failed to upload image, image size is more than 2,5MB.');
        }
        const data: FormData = new FormData();

        data.append('image', image);

        try {
            const pictureData = await api.post<string>(backendApiUrl.user.profileImage, data);

            return pictureData ? pictureData : Promise.reject('No picture data.');
        } catch (e) {
            return Promise.reject(e);
        }
    },

    saveUserLanguage: async (language: string): Promise<void> => {
        const response = await fetch(backendApiUrl.user.language, {
            method: 'POST',
            headers: {
                language: language,
            },
        });

        return response.ok ? Promise.resolve() : Promise.reject(new Error('Failed to save the language.'));
    },

    /**
     * Updates the user's picture in the backend.
     */
    updateUserStampPicture: async (image: File, thunkApi): Promise<string> => {
        /** Note: Backend only accepts data in form-format! */

        if (image.size > (thunkApi.getState() as IGlobalStore).userSettings.maxStampPictureSize) {
            return Promise.reject('Failed to upload image, image size is more than 2,5MB.');
        }
        const data: FormData = new FormData();

        // ToDO: use UUID
        GlobalVariables.userStamp.id = uuidv4();
        data.append('metadata', JSON.stringify(GlobalVariables.userStamp));
        data.append('stamp', image);

        try {
            const response = await api.post<string>('stamps/save', data);

            if (response !== '') {
                return Promise.reject('No picture data.');
            }
            ReduxUpdater.addUserStamp({
                category: GlobalVariables.userStamp.category,
                height: GlobalVariables.userStamp.height,
                id: GlobalVariables.userStamp.id,
                name: GlobalVariables.userStamp.name,
                scale: GlobalVariables.userStamp.scale,
                width: GlobalVariables.userStamp.width,
            } as IUserStamp);
            return response;
        } catch (e) {
            return Promise.reject(e);
        }
    },

    /**
     * Updates the user's picture in the backend.
     */
    deleteUserStampPicture: async (stampId: string): Promise<string> => {
        try {
            const response = await api.get<string>('stamps/delete', {
                headers: {
                    stampKey: stampId,
                },
            });

            if (response !== '') {
                return Promise.reject('No picture deleted.');
            }
            ReduxUpdater.removeUserStamps([stampId]);
            return response;
        } catch (e) {
            return Promise.reject(e);
        }
    },

    /**
     * Logs the user out of the application.
     */
    logoutUser: async (): Promise<void> => {
        const response = await fetch(backendApiUrl.user.logout, { method: 'POST' });

        return response.ok ? Promise.resolve() : Promise.reject(response.status);
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IUserSettingsState>) => {
        const onLoad = state => {
            state.error = null;
            state.loading = true;
        };

        const onError = (state, action) => {
            state.loading = false;
            state.data = null;
            state.error = action.error;
        };

        /**
         * Reducers for the 'getUserProfile' thunk.
         */
        builder.addCase(getUserProfile.pending, onLoad);
        builder.addCase(getUserProfile.fulfilled, (state, action) => {
            state.data = action.payload;
            state.error = null;
            state.loading = false;
        });
        builder.addCase(getUserProfile.rejected, onError);

        /**
         * Reducers for the 'updateUserProfileProtection' thunk.
         */
        builder.addCase(updateUserProfileProtection.pending, (state: IUserSettingsState) => {
            state.error = null;
            state.picture.error = null;
        });
        builder.addCase(updateUserProfileProtection.fulfilled, (state, action) => {
            if (state.data && GlobalVariables.authenticatedUser) {
                state.data.dataProtection = action.payload;
            }

            state.error = null;
        });
        builder.addCase(updateUserProfileProtection.rejected, (state, action) => {
            state.error = action.error;
        });

        /**
         * Reducers for the 'updateUserProfilePicture' thunk.
         */
        builder.addCase(updateUserProfilePicture.pending, (state: IUserSettingsState) => {
            state.picture.loading = true;
            state.picture.error = null;
            state.error = null;
        });
        builder.addCase(updateUserProfilePicture.fulfilled, (state, action) => {
            if (state.data && GlobalVariables.authenticatedUser) {
                state.data.picture = action.payload;
            }

            state.picture.loading = false;
        });
        builder.addCase(updateUserProfilePicture.rejected, (state, action) => {
            state.picture.error = action.error;
            state.picture.loading = false;
        });

        /**
         * Reducers for the 'logoutUser' thunk.
         */
        builder.addCase(logoutUser.pending, onLoad);
        builder.addCase(logoutUser.fulfilled, () => {
            window.location.href = auth0LogoutUri;
        });
        builder.addCase(logoutUser.rejected, onError);
    },
};

/**
 * Interface that describes the state object for this redux slice.
 */
export type IUserSettingsState = SlicePropsWithData<OAuthUserDataType | null> & {
    /** Max size in Bytes of image that is allowed for downloading. */
    maxProfilePictureSize: number;

    /** Max size in Bytes of image that is allowed for downloading. */
    maxStampPictureSize: number;

    /** Defines loading and error state for the user avatar. */
    picture: SliceProps;
};

/**
 * Expose thunk functions as exports.
 */
export const getUserProfile = createAsyncThunk('user/get', thunks.getUserProfile);
export const updateUserProfileProtection = createAsyncThunk(
    'user/post/protection',
    thunks.updateUserProfileProtection,
);
export const updateUserProfilePicture = createAsyncThunk(
    'user/post/image',
    thunks.updateUserProfilePicture,
);

export const uploadUserStampPicture = createAsyncThunk(
    'user/stamp/save',
    thunks.updateUserStampPicture,
);

export const deleteUserStampPicture = createAsyncThunk(
    'user/stamp/delete',
    thunks.deleteUserStampPicture,
);

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    clearErrors(state: IUserSettingsState) {
        state.error = null;
        state.picture.error = null;
    },
};

/**
 * Create the redux slice.
 */
export const userSettingsSlice = createSlice<
    IUserSettingsState,
    SliceCaseReducers<IUserSettingsState>
>({
    name: 'userSettings',
    initialState: initialState,
    reducers,
    extraReducers: thunks._reducers,
});

/**
 * Export functions from slice actions.
 */
export const { setAuthData, clearErrors } = userSettingsSlice.actions;

export const userSettingsError = (state: IGlobalStoreReducers) => state.userSettings.error;

/**
 * Export slice reducer as default.
 */
export default userSettingsSlice.reducer;

export const logoutUser = createAsyncThunk('user/logout', thunks.logoutUser);
export const saveUserLanguage = createAsyncThunk('user/language', thunks.saveUserLanguage);
